import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ShieldCheckIcon } from 'lucide-react';
import { getDomain, getProgramsByDomain, programDomains, programTemplates } from '../data/programTemplates';
import { LegalFooter } from '../components/LegalFooter';
import { HeroMatchForm } from '../components/HeroMatchForm';
import { Logo } from '../components/Logo';
import { usePageMeta } from '../hooks/usePageMeta';
export function ProgramDomainsPage() {
  usePageMeta('New Online Programs | Six Strategic Domains', 'Explore new online healthcare and business programs organized into six strategic domains — from clinical care and healthcare operations to data, community health, and leadership.', {
    ogType: 'website'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const scrollToForm = () => {
    document.getElementById('hero-match-form')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  return <main className="min-h-screen bg-white" data-id="element-2397" data-mp="1kw9ryvm28q3s">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-100" data-id="element-2398" data-mp="1ktvz8teurpze">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between" data-id="element-2399" data-mp="1owk3ensmg8q1">
          <Link to="/" className="flex items-center" aria-label="findonlinecolleges.org home" data-id="element-2400" data-mp="1ax10c9eo3nkl">
            <Logo data-id="element-2401" data-mp="1fyq91ief3p5i" />
          </Link>
          <div className="hidden sm:flex items-center text-xs font-semibold tracking-wider uppercase text-navy" data-id="element-2402" data-mp="117r4qjkm7mrq">
            <ShieldCheckIcon className="w-4 h-4 mr-1.5 text-crimson" aria-hidden="true" data-id="element-2403" data-mp="15zizl6at0eno" />
            Accredited Programs Only
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-navy" data-id="element-2404" data-mp="x6nqu2m4o0t5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20" data-id="element-2405" data-mp="i9exj3d9kmys">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-start" data-id="element-2406" data-mp="ibhlziyrq81e">
            <div className="lg:col-span-3" data-id="element-2407" data-mp="n7gcbc3nq04g">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-gold mb-4" data-id="element-2408" data-mp="444ujiu61v35">
                {programTemplates.length} Programs · {programDomains.length}{' '}
                Domains
              </p>
              <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-6" data-id="element-2409" data-mp="3qk914yhzgh4b">
                New Online Programs
              </h1>
              <p className="text-lg text-white/80 leading-relaxed" data-id="element-2410" data-mp="3li9wzmef5g6b">
                New online programs are organized into six strategic domains
                that reflect key workforce needs across healthcare and related
                business functions. This structure clarifies how each program
                aligns to a broader capability area — from direct care support
                and healthcare operations to data, community health, and
                leadership.
              </p>
            </div>

            <div id="hero-match-form" className="lg:col-span-2" data-id="element-2411" data-mp="2svp6sp9oja4c">
              <HeroMatchForm programs={programTemplates} domainNameFor={program => getDomain(program.domainSlug)?.name} data-id="element-2412" data-mp="2su4fd8inw7ap" />
            </div>
          </div>
        </div>
      </section>

      {/* Domains */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-14" data-id="element-2413" data-mp="1ibax7k1h46yn">
        {programDomains.map((domain, idx) => {
        const programs = getProgramsByDomain(domain.slug);
        return <section key={domain.slug} aria-labelledby={`domain-${domain.slug}`} data-id="element-2414" data-mp="1s0cdzcfuesdo" data-mpi={idx}>
              <div className="flex items-baseline gap-4 mb-3" data-id="element-2415" data-mp="1w2fz6ssbdbc5">
                <span className="text-sm font-bold tabular-nums text-crimson" data-id="element-2416" data-mp="1w4tv13utirkr">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <h2 id={`domain-${domain.slug}`} className="text-2xl sm:text-3xl font-bold text-navy" data-id="element-2417" data-mp="2603ubjtu0kuu">
                  {domain.name}
                </h2>
              </div>
              <p className="text-gray-600 leading-relaxed max-w-3xl mb-6 pl-9" data-id="element-2418" data-mp="bne0f23grbng">
                {domain.description}
              </p>
              <ul className="grid sm:grid-cols-2 gap-4 pl-9" data-id="element-2419" data-mp="1x5esse52wech">
                {programs.map(program => <li key={program.slug} className="flex" data-id="element-2420" data-mp="2b5fsvrdirhqw">
                    <Link to={`/program-information/${program.slug}`} className="group border border-gray-200 rounded-xl p-5 flex flex-col w-full hover:border-navy transition-colors duration-200" data-id="element-2421" data-mp="2b2tlpsujj497">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2" data-id="element-2422" data-mp="1efyg6sg4cvfp">
                        {program.credential}
                      </span>
                      <span className="font-bold text-navy mb-2" data-id="element-2423" data-mp="1nf3blpwdw8nn">
                        {program.name}
                      </span>
                      <span className="text-sm text-gray-600 leading-relaxed mb-4" data-id="element-2424" data-mp="znxz9kpnjyi3">
                        {program.weeks} weeks · {program.credits} credits ·{' '}
                        {program.instructionalHours.toLocaleString()} hours
                      </span>
                      <span className="mt-auto inline-flex items-center text-sm font-semibold text-crimson" data-id="element-2425" data-mp="qc0jc5vl55d3">
                        View program
                        <ArrowRightIcon className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform duration-200" aria-hidden="true" data-id="element-2426" data-mp="bk83ztvsjrbd" />
                      </span>
                    </Link>
                  </li>)}
              </ul>
            </section>;
      })}
      </div>

      {/* Bottom CTA */}
      <section className="bg-navy py-14" data-id="element-2427" data-mp="zbn9fviv6oqu">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2428" data-mp="zac7m18zbr2d">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-2429" data-mp="3f2i7td92oy2l">
            Find the Pathway That Fits Your Goals
          </h2>
          <p className="text-white/80 leading-relaxed mb-8" data-id="element-2430" data-mp="3k2nusg4edwec">
            Get matched with accredited schools offering these programs. No cost,
            and no obligation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2431" data-mp="1ll3u9kiyn673">
            <button type="button" onClick={scrollToForm} className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-10 rounded-lg text-sm tracking-wide uppercase transition-colors duration-200" data-id="element-2432" data-mp="1gskfik8je50p">
              Get Matched
              <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2433" data-mp="12srun5joyj0n" />
            </button>
            <Link to="/request-information" className="text-sm font-semibold text-white/80 hover:text-white underline underline-offset-4 transition-colors duration-200" data-id="element-2434" data-mp="xvj86srifstl">
              Or request information
            </Link>
          </div>
        </div>
      </section>

      <LegalFooter data-id="element-2435" data-mp="2napn28dcwl7z" />
    </main>;
}